<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type {
  PartsTreeNode,
  FieldPartTemplate,
} from "@/types/field-observations.types";
import {
  useCreateFieldPartTemplate,
  useUpdateFieldPartTemplate,
} from "@/stores/queries/field-observations.query";
import StructureTemplateEditor from "./StructureTemplateEditor.vue";
import DRBtn from "@/components/UI/DRBtn.vue";

const props = defineProps<{
  modelValue: boolean;
  template?: FieldPartTemplate;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const { t } = useTranslation();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const isEditMode = computed(() => !!props.template);

// Form state
const name = ref("");
const description = ref("");
const tree = ref<PartsTreeNode[]>([]);

// Initialize form when drawer opens
watch(
  () => props.modelValue,
  (open) => {
    if (open && props.template) {
      // Edit mode: pre-fill with template data
      name.value = props.template.name;
      description.value = props.template.description || "";
      tree.value = JSON.parse(JSON.stringify(props.template.tree.parts));
    } else if (!open) {
      // Reset form when drawer closes
      name.value = "";
      description.value = "";
      tree.value = [];
    }
  }
);

// Mutations
const { mutateAsync: createTemplate, isPending: isCreating } =
  useCreateFieldPartTemplate();
const { mutateAsync: updateTemplate, isPending: isUpdating } =
  useUpdateFieldPartTemplate();

const isSaving = computed(() => isCreating.value || isUpdating.value);

const canSave = computed(() => {
  return name.value.trim() && tree.value.length > 0;
});

const handleSave = async () => {
  if (!canSave.value) return;

  if (isEditMode.value && props.template) {
    await updateTemplate({
      id: props.template.id,
      payload: {
        name: name.value.trim(),
        description: description.value.trim() || undefined,
        tree: { parts: tree.value },
      },
    });
  } else {
    await createTemplate({
      name: name.value.trim(),
      description: description.value.trim() || undefined,
      tree: { parts: tree.value },
    });
  }

  isOpen.value = false;
};

const drawerTitle = computed(() => {
  return isEditMode.value
    ? t("dashboard:fieldObservations.partTemplates.edit.title")
    : t("dashboard:fieldObservations.partTemplates.create.title");
});
</script>

<template>
  <el-drawer
    v-model="isOpen"
    :title="drawerTitle"
    size="500px"
    :close-on-click-modal="false"
  >
    <div class="drawer-content">
      <el-form label-position="top">
        <el-form-item
          :label="t('dashboard:fieldObservations.partTemplates.create.name')"
          required
        >
          <el-input
            v-model="name"
            :placeholder="
              t(
                'dashboard:fieldObservations.partTemplates.saveModal.namePlaceholder'
              )
            "
          />
        </el-form-item>

        <el-form-item
          :label="
            t('dashboard:fieldObservations.partTemplates.create.description')
          "
        >
          <el-input
            v-model="description"
            type="textarea"
            :rows="2"
            :placeholder="
              t(
                'dashboard:fieldObservations.partTemplates.saveModal.descriptionPlaceholder'
              )
            "
          />
        </el-form-item>

        <el-form-item
          :label="
            t('dashboard:fieldObservations.partTemplates.create.structure')
          "
          required
        >
          <div class="structure-editor-wrapper">
            <StructureTemplateEditor v-model="tree" />
          </div>
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <div class="d-flex gap-2 justify-content-end">
        <DRBtn
          variant="outline"
          :label="t('common:cancel')"
          @click="isOpen = false"
        />
        <DRBtn
          variant="primary"
          icon="check"
          :label="t('common:save')"
          :loading="isSaving"
          :disabled="!canSave"
          @click="handleSave"
        />
      </div>
    </template>
  </el-drawer>
</template>

<style lang="scss" scoped>
.drawer-content {
  padding: 0;
}

.structure-editor-wrapper {
  width: 100%;
}

:deep(.el-drawer__header) {
  border-bottom: 1px solid var(--bs-border-color);
  margin-bottom: 0;
  padding-bottom: 16px;
}

:deep(.el-drawer__footer) {
  border-top: 1px solid var(--bs-border-color);
  padding-top: 16px;
}
</style>
